import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  CircularProgress,
  Alert,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  CalendarToday as CalendarIcon,
  GetApp as GetAppIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { useAccesos } from '../contexts/AccesosContext';

const ReporteCxCSaldos = () => {
  const server = process.env.REACT_APP_API_ROUTE;
  const { tienePermiso } = useAccesos();

  const [resumen, setResumen] = useState([]);
  const [detalle, setDetalle] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sucursales, setSucursales] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);

  // Filtros
  const [fechaInicio, setFechaInicio] = useState(() => {
    const fecha = new Date();
    fecha.setDate(fecha.getDate() - 30);
    return fecha.toISOString().split('T')[0];
  });
  const [fechaFin, setFechaFin] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [idSucursal, setIdSucursal] = useState('');
  const [tipoPoliza, setTipoPoliza] = useState('1');
  const [mayorA, setMayorA] = useState('');

  // Tabla Resumen
  const [pageR, setPageR] = useState(0);
  const [rowsPerPageR, setRowsPerPageR] = useState(25);
  const [orderByR, setOrderByR] = useState('saldo');
  const [orderR, setOrderR] = useState('desc');

  // Tabla Detalle
  const [orderByD, setOrderByD] = useState('fechaFactura');
  const [orderD, setOrderD] = useState('desc');

  useEffect(() => {
    cargarSucursales();
  }, []);

  const cargarSucursales = async () => {
    try {
      const response = await fetch(`${server}/MgSucursales`);
      if (response.ok) {
        const data = await response.json();
        let arr = Array.isArray(data) ? data : data?.$values || [];
        setSucursales(arr);
      }
    } catch (err) {
      console.error('Error al cargar sucursales:', err);
    }
  };

  const handleBuscar = async () => {
    if (fechaInicio && fechaFin && new Date(fechaInicio) > new Date(fechaFin)) {
      alert('La fecha fin no puede ser menor a la fecha inicio');
      return;
    }

    setLoading(true);
    setError(null);
    setPageR(0);
    setClienteSeleccionado(null);

    try {
      const sucId = idSucursal || 0;
      const f1 = fechaInicio.replace(/-/g, '');
      const f2 = fechaFin.replace(/-/g, '');
      const response = await fetch(
        `${server}/CxC/cxc-saldos/${sucId}/${f1}/${f2}/${tipoPoliza}`
      );

      if (response.ok) {
        const data = await response.json();
        const res = Array.isArray(data.resumen) ? data.resumen : data.resumen?.$values || [];
        const det = Array.isArray(data.detalle) ? data.detalle : data.detalle?.$values || [];
        setResumen(res);
        setDetalle(det);
      } else if (response.status === 404) {
        setResumen([]);
        setDetalle([]);
        setError('No se encontraron datos para los filtros seleccionados.');
      } else {
        const msg = await response.text();
        setError(msg || 'Error al obtener los datos.');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Error de conexión con el servidor.');
    } finally {
      setLoading(false);
    }
  };

  const handleLimpiar = () => {
    const fechaFinNueva = new Date();
    const fechaInicioNueva = new Date();
    fechaInicioNueva.setDate(fechaInicioNueva.getDate() - 30);

    setFechaInicio(fechaInicioNueva.toISOString().split('T')[0]);
    setFechaFin(fechaFinNueva.toISOString().split('T')[0]);
    setIdSucursal('');
    setTipoPoliza('1');
    setMayorA('');
    setResumen([]);
    setDetalle([]);
    setError(null);
    setClienteSeleccionado(null);
  };

  // Ordenamiento
  const sortData = (data, orderBy, order) => {
    if (!data || data.length === 0) return [];
    return [...data].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];
      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }
      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleRequestSortR = (property) => {
    const isAsc = orderByR === property && orderR === 'asc';
    setOrderR(isAsc ? 'desc' : 'asc');
    setOrderByR(property);
  };

  const handleRequestSortD = (property) => {
    const isAsc = orderByD === property && orderD === 'asc';
    setOrderD(isAsc ? 'desc' : 'asc');
    setOrderByD(property);
  };

  // Filtrar resumen por "Mayores a"
  const resumenFiltrado = useMemo(() => {
    if (!mayorA || isNaN(parseFloat(mayorA))) return resumen;
    const umbral = parseFloat(mayorA);
    return resumen.filter(r => r.saldo >= umbral);
  }, [resumen, mayorA]);

  const sortedResumen = useMemo(() => sortData(resumenFiltrado, orderByR, orderR), [resumenFiltrado, orderR, orderByR]);
  const paginatedResumen = useMemo(() => sortedResumen.slice(pageR * rowsPerPageR, pageR * rowsPerPageR + rowsPerPageR), [sortedResumen, pageR, rowsPerPageR]);

  // Detalle filtrado por cliente seleccionado
  const detalleCliente = useMemo(() => {
    if (!clienteSeleccionado) return [];
    return detalle.filter(d => d.idCliente === clienteSeleccionado.idCliente);
  }, [detalle, clienteSeleccionado]);

  const sortedDetalle = useMemo(() => sortData(detalleCliente, orderByD, orderD), [detalleCliente, orderByD, orderD]);

  const totalesDetalleCliente = useMemo(() => {
    if (detalleCliente.length === 0) return { valorFactura: 0, abonos: 0, saldo: 0 };
    return detalleCliente.reduce(
      (acc, row) => ({
        valorFactura: acc.valorFactura + (row.valorFactura || 0),
        abonos: acc.abonos + (row.abonos || 0),
        saldo: acc.saldo + (row.saldo || 0)
      }),
      { valorFactura: 0, abonos: 0, saldo: 0 }
    );
  }, [detalleCliente]);

  // Totales resumen
  const totalResumen = useMemo(() => {
    if (!resumenFiltrado || resumenFiltrado.length === 0) return 0;
    return resumenFiltrado.reduce((acc, row) => acc + (row.saldo || 0), 0);
  }, [resumenFiltrado]);

  const formatNumber = (value) => {
    if (value === null || value === undefined) return 'L 0.00';
    return new Intl.NumberFormat('es-HN', {
      style: 'currency',
      currency: 'HNL'
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (date.getFullYear() <= 1900) return '';
    return date.toLocaleDateString('es-HN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // Exportar Excel (solo detalle del cliente seleccionado)
  const exportarExcel = () => {
    if (!clienteSeleccionado || detalleCliente.length === 0) return;
    const wb = XLSX.utils.book_new();

    const datosDetalle = detalleCliente.map((row) => ({
      'Fecha Factura': formatDate(row.fechaFactura),
      'Órdenes': row.ordenes,
      'Valor Factura': row.valorFactura,
      'Abonos': row.abonos,
      'Saldo': row.saldo,
      'Días Crédito': row.diasCredito,
      'Días Deuda': row.diasDeuda,
      'Días Restantes': row.diasRestantes,
      'Días Vencido': row.diasVencido,
      'Póliza': row.polizaDirecta ? 'Directa' : 'Tercerizada',
      'Empresa': row.empresa || ''
    }));
    datosDetalle.push({
      'Fecha Factura': 'TOTALES', 'Órdenes': '',
      'Valor Factura': totalesDetalleCliente.valorFactura,
      'Abonos': totalesDetalleCliente.abonos,
      'Saldo': totalesDetalleCliente.saldo,
      'Días Crédito': '', 'Días Deuda': '', 'Días Restantes': '', 'Días Vencido': '', 'Póliza': '', 'Empresa': ''
    });
    const wsD = XLSX.utils.json_to_sheet(datosDetalle);
    wsD['!cols'] = [
      { wch: 14 }, { wch: 20 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
      { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 20 }
    ];
    XLSX.utils.book_append_sheet(wb, wsD, clienteSeleccionado.nombre || 'Detalle');

    XLSX.writeFile(wb, `CxC_${clienteSeleccionado.nombre || 'Detalle'}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const hayDatos = resumen.length > 0;

  return (
    <div className="right_col" role="main" style={{ minHeight: '100vh' }}>
      <div className="">
        <div className="page-title">
          <div className="title_left">
            <h3>Listado de Deudas Adquiridas por los Clientes</h3>
          </div>
        </div>
        <div className="clearfix"></div>

        <div className="row">
          <div className="col-md-12 col-sm-12">
            <Paper sx={{ p: 3 }}>

              {/* Filtros */}
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Búsqueda Rápida:
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      label="Desde"
                      type="date"
                      size="small"
                      value={fechaInicio}
                      onChange={(e) => setFechaInicio(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      disabled={loading}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      label="Hasta"
                      type="date"
                      size="small"
                      value={fechaFin}
                      onChange={(e) => setFechaFin(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      disabled={loading}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      label="Mayores a"
                      type="number"
                      size="small"
                      value={mayorA}
                      onChange={(e) => setMayorA(e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">L</InputAdornment>
                      }}
                      disabled={loading}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth disabled={loading} size="small">
                      <InputLabel id="sucursal-label">Sucursal</InputLabel>
                      <Select
                        labelId="sucursal-label"
                        value={idSucursal || ''}
                        label="Sucursal"
                        onChange={(e) => setIdSucursal(e.target.value)}
                        MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                      >
                        <MenuItem value="">
                          <em>Todas las sucursales</em>
                        </MenuItem>
                        {sucursales.map((s) => (
                          <MenuItem key={s.idSucursal} value={s.idSucursal}>
                            {s.nombreSucursal}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SearchIcon />}
                        onClick={handleBuscar}
                        disabled={loading || !tienePermiso('Buscar Reporte CxC')}
                        size="small"
                      >
                        Buscar
                      </Button>
                      <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<ClearIcon />}
                        onClick={handleLimpiar}
                        disabled={loading}
                        size="small"
                      >
                        Limpiar
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Polizas de aseguradora */}
              <Box sx={{ mb: 2 }}>
                <FormControl component="fieldset" disabled={loading}>
                  <FormLabel component="legend" sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                    Pólizas de aseguradora:
                  </FormLabel>
                  <RadioGroup
                    row
                    value={tipoPoliza}
                    onChange={(e) => setTipoPoliza(e.target.value)}
                  >
                    <FormControlLabel value="1" control={<Radio size="small" />} label="Ambas" />
                    <FormControlLabel value="2" control={<Radio size="small" />} label="Directa" />
                    <FormControlLabel value="3" control={<Radio size="small" />} label="Tercerizada" />
                  </RadioGroup>
                </FormControl>
              </Box>

              {/* Loading */}
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              )}

              {/* Error */}
              {error && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {/* ═══════════ TABLA CLIENTES ═══════════ */}
              {!loading && hayDatos && (
                <>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 350, mb: 0 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell style={{ fontWeight: 'bold', minWidth: 140 }}>
                            <TableSortLabel
                              active={orderByR === 'dniRtn'}
                              direction={orderByR === 'dniRtn' ? orderR : 'asc'}
                              onClick={() => handleRequestSortR('dniRtn')}
                            >
                              R.T.N.
                            </TableSortLabel>
                          </TableCell>
                          <TableCell style={{ fontWeight: 'bold', minWidth: 250 }}>
                            <TableSortLabel
                              active={orderByR === 'nombre'}
                              direction={orderByR === 'nombre' ? orderR : 'asc'}
                              onClick={() => handleRequestSortR('nombre')}
                            >
                              Nombre del Cliente
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right" style={{ fontWeight: 'bold', minWidth: 130 }}>
                            <TableSortLabel
                              active={orderByR === 'saldo'}
                              direction={orderByR === 'saldo' ? orderR : 'asc'}
                              onClick={() => handleRequestSortR('saldo')}
                            >
                              Saldo Actual
                            </TableSortLabel>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedResumen.map((row, index) => (
                          <TableRow
                            hover
                            key={row.idCliente || index}
                            onClick={() => setClienteSeleccionado(row)}
                            selected={clienteSeleccionado?.idCliente === row.idCliente}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>{row.dniRtn}</TableCell>
                            <TableCell>{row.nombre}</TableCell>
                            <TableCell align="right">{formatNumber(row.saldo)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Total y paginación */}
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: '#e8e8e8',
                    px: 2,
                    py: 1,
                    borderLeft: '1px solid #ddd',
                    borderRight: '1px solid #ddd',
                    borderBottom: '1px solid #ddd'
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      TOTAL DEUDA ACTUAL: &nbsp; {formatNumber(totalResumen)}
                    </Typography>
                    <Typography variant="body2">
                      {resumenFiltrado.length} cliente(s)
                    </Typography>
                  </Box>

                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={sortedResumen.length}
                    rowsPerPage={rowsPerPageR}
                    page={pageR}
                    onPageChange={(e, p) => setPageR(p)}
                    onRowsPerPageChange={(e) => { setRowsPerPageR(parseInt(e.target.value, 10)); setPageR(0); }}
                    labelRowsPerPage="Filas por página:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                  />

                  {/* ═══════════ DETALLE DEL CLIENTE SELECCIONADO ═══════════ */}
                  {clienteSeleccionado && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          Listado de Facturas Pendientes de: {clienteSeleccionado.nombre}
                        </Typography>
                        <Button
                          variant="outlined"
                          color="primary"
                          startIcon={<GetAppIcon />}
                          onClick={exportarExcel}
                          size="small"
                          disabled={!tienePermiso('Exportar Excel CxC')}
                        >
                          Excel
                        </Button>
                      </Box>

                      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 350 }}>
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell style={{ fontWeight: 'bold' }}>
                                <TableSortLabel
                                  active={orderByD === 'fechaFactura'}
                                  direction={orderByD === 'fechaFactura' ? orderD : 'asc'}
                                  onClick={() => handleRequestSortD('fechaFactura')}
                                >
                                  Fecha
                                </TableSortLabel>
                              </TableCell>
                              <TableCell style={{ fontWeight: 'bold' }}>Órdenes</TableCell>
                              <TableCell align="right" style={{ fontWeight: 'bold' }}>
                                <TableSortLabel
                                  active={orderByD === 'valorFactura'}
                                  direction={orderByD === 'valorFactura' ? orderD : 'asc'}
                                  onClick={() => handleRequestSortD('valorFactura')}
                                >
                                  Valor Factura
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="right" style={{ fontWeight: 'bold' }}>
                                <TableSortLabel
                                  active={orderByD === 'abonos'}
                                  direction={orderByD === 'abonos' ? orderD : 'asc'}
                                  onClick={() => handleRequestSortD('abonos')}
                                >
                                  Abonos
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="right" style={{ fontWeight: 'bold' }}>
                                <TableSortLabel
                                  active={orderByD === 'saldo'}
                                  direction={orderByD === 'saldo' ? orderD : 'asc'}
                                  onClick={() => handleRequestSortD('saldo')}
                                >
                                  Saldo Actual
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="center" style={{ fontWeight: 'bold' }}>Crédito</TableCell>
                              <TableCell align="center" style={{ fontWeight: 'bold' }}>
                                <TableSortLabel
                                  active={orderByD === 'diasDeuda'}
                                  direction={orderByD === 'diasDeuda' ? orderD : 'asc'}
                                  onClick={() => handleRequestSortD('diasDeuda')}
                                >
                                  Días Deuda
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="center" style={{ fontWeight: 'bold' }}>Días Restantes</TableCell>
                              <TableCell align="center" style={{ fontWeight: 'bold' }}>
                                <TableSortLabel
                                  active={orderByD === 'diasVencido'}
                                  direction={orderByD === 'diasVencido' ? orderD : 'asc'}
                                  onClick={() => handleRequestSortD('diasVencido')}
                                >
                                  Días Vencidos
                                </TableSortLabel>
                              </TableCell>
                              <TableCell align="center" style={{ fontWeight: 'bold' }}>Póliza</TableCell>
                              <TableCell style={{ fontWeight: 'bold' }}>Empresa</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sortedDetalle.map((d, i) => (
                              <TableRow
                                key={i}
                                sx={d.diasVencido > 0 ? { backgroundColor: '#fff3e0' } : {}}
                              >
                                <TableCell>{formatDate(d.fechaFactura)}</TableCell>
                                <TableCell>{d.ordenes || '-'}</TableCell>
                                <TableCell align="right">{formatNumber(d.valorFactura)}</TableCell>
                                <TableCell align="right">{formatNumber(d.abonos)}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                  {formatNumber(d.saldo)}
                                </TableCell>
                                <TableCell align="center">{d.diasCredito} DÍAS</TableCell>
                                <TableCell align="center">{d.diasDeuda} DÍAS</TableCell>
                                <TableCell align="center">{d.diasRestantes} DÍAS</TableCell>
                                <TableCell
                                  align="center"
                                  sx={d.diasVencido > 0 ? { color: 'red', fontWeight: 'bold' } : {}}
                                >
                                  {d.diasVencido > 0 ? `${d.diasVencido} DÍAS` : '-'}
                                </TableCell>
                                <TableCell align="center">
                                  {d.polizaDirecta ? 'Directa' : 'Tercerizada'}
                                </TableCell>
                                <TableCell>{d.empresa || '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {/* Totales del detalle */}
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        backgroundColor: '#e8e8e8',
                        px: 2,
                        py: 1,
                        borderLeft: '1px solid #ddd',
                        borderRight: '1px solid #ddd',
                        borderBottom: '1px solid #ddd'
                      }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          TOTAL FACTURAS: {detalleCliente.length}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 3 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            Valor: {formatNumber(totalesDetalleCliente.valorFactura)}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            Abonos: {formatNumber(totalesDetalleCliente.abonos)}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            Saldo: {formatNumber(totalesDetalleCliente.saldo)}
                          </Typography>
                        </Box>
                      </Box>
                    </>
                  )}
                </>
              )}

              {/* Estado vacío */}
              {!loading && !error && !hayDatos && (
                <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                  <Typography variant="h6">
                    Seleccione los filtros y presione Buscar
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Rango máximo permitido: 60 días
                  </Typography>
                </Box>
              )}
            </Paper>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReporteCxCSaldos;
